<script lang="ts">
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Input from "@components/form/Input.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import pinIcon from "@core/assets/icons/pin.svg?raw";
	import type { PublicCourseTerm } from "@core/schema/CourseTerm.js";
	import { CourseType } from "@core/schema/CourseType.js";
	import { Currency } from "@core/schema/Currency.js";
	import { calculateCourseOrderPrice } from "@core/schema/utils/calculateCourseOrderPrice.js";
	import { isTermFull } from "@core/schema/utils/isTermFull.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { toDateTimeMonthYearFormat } from "@core/utils/toDateTimeMonthYearFormat.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { selectedCurrency } from "../../state/selectedCurrency.svelte.js";
	import translations from "./CourseTermPicker.translations.json" with { type: "json" };

	const collapsedTermsLimit = 4;
	const lowSeatsThreshold = 3;

	const {
		terms,
		selectedTermId,
		onselect,
	}: {
		terms: PublicCourseTerm[];
		selectedTermId: string | undefined;
		onselect(this: void, term: PublicCourseTerm): void;
	} = $props();
	const translate = getTranslate(translations);
	const { tenant, exchangeRatesSetting } = getGlobalTemplateProps();
	const currency = $derived(selectedCurrency.current ?? tenant.defaultCurrency);
	const inputName = $props.id();

	function formatTermPrice(term: PublicCourseTerm): string {
		const unitPrice = calculateCourseOrderPrice(term, currency, exchangeRatesSetting?.exchangeRates ?? []);
		return currency === Currency.CZK
			? formatPrice(Math.ceil(unitPrice.withTax), currency, tenant.locale, false)
			: formatPrice(unitPrice.withTax, currency, tenant.locale, true);
	}

	let expanded = $state(false);
	const selectedIndex = $derived(terms.findIndex(({ id }) => id === selectedTermId));
	const selectedBehindFold = $derived(selectedIndex >= collapsedTermsLimit);
	const visibleTerms = $derived(expanded || selectedBehindFold ? terms : terms.slice(0, collapsedTermsLimit));
</script>

<div class="flex flex-col gap-2" role="radiogroup">
	{#each visibleTerms as term (term.id)}
		{@const full = isTermFull(term)}
		{@const seatsLeft = term.capacity - term.participantsCount}
		{@const showsPrice = term.courseType === CourseType.Workshop && !full}
		<label
			class={[
				"flex items-center gap-4 rounded-2xl border border-fcrlightgray-300 bg-fcrlightgray-200 px-4 py-3",
				"cursor-pointer transition-colors has-[input:checked]:border-transparent has-[input:checked]:outline outline-fcrdarkgray",
				"has-[input:disabled]:cursor-not-allowed has-[input:disabled]:opacity-60",
				"[&:not(:has(input:disabled)):not(:has(input:checked))]:hover:border-fcrdarkgray",
			]}
		>
			<Input
				type="radio"
				name={inputName}
				value={term.id}
				group={selectedTermId}
				disabled={full}
				onchange={(): void => {
					onselect(term);
				}}
			/>
			<span class="flex min-w-0 grow flex-col">
				<span class="font-medium text-sm/normal md:text-base text-fcrdarkgray">
					{toDateTimeMonthYearFormat(term.startsAt, tenant.locale)}
				</span>
				<span class="flex min-w-0 items-center gap-1.5 text-sm/normal text-fcrgray">
					<Icon icon={pinIcon} class="size-3.5" />
					<span class="truncate">{term.placeName}</span>
				</span>
			</span>
			<span
				class={[
					"shrink-0 rounded-full border-[1.5px] px-3 py-0 uppercase font-antipol text-2.5/[1.15rem]",
					full
						? "border-fcrerror/40 text-fcrerror"
						: !showsPrice && seatsLeft <= lowSeatsThreshold
							? "border-fcrorange/40 text-fcrorange"
							: "border-fcrlightgray-300 text-fcrdarkgray",
				]}
			>
				{#if showsPrice}
					<Formatted text={formatTermPrice(term)} />
				{:else}
					<Formatted text={full ? translate("full") : translate("seatsLeft", { count: seatsLeft })} />
				{/if}
			</span>
		</label>
	{/each}

	{#if terms.length > collapsedTermsLimit && !selectedBehindFold}
		<button
			type="button"
			class="self-start mt-1 text-sm/normal text-fcrgray underline transition-colors hover:text-fcrdarkgray"
			onclick={(): void => {
				expanded = !expanded;
			}}
		>
			<Formatted
				text={expanded ? translate("showLess") : translate("showAll", { count: terms.length - collapsedTermsLimit })}
			/>
		</button>
	{/if}
</div>
